// frontend/src/utils/permissions.js
//
// Mirror of backend/src/middleware/permissions.js — kept in sync manually.
// Used for instant client-side checks (hide buttons, gate UI). The backend
// remains the source of truth; this is purely a UX optimization so the
// admin doesn't see buttons that would just throw 403.

export const ADMIN_PANEL_ROLES = ['admin', 'manager', 'staff', 'cashier', 'chef', 'waiter'];

export const PERMISSIONS = {
  'menu:read':    ['admin', 'manager', 'staff', 'cashier', 'chef', 'waiter', 'customer', 'guest'],
  'menu:create':  ['admin', 'manager'],
  'menu:update':  ['admin', 'manager'],
  'menu:delete':  ['admin', 'manager'],
  'menu:toggle':  ['admin', 'manager', 'chef'],

  'order:read':   ['admin', 'manager', 'staff', 'cashier', 'waiter', 'customer'],
  'order:create': ['admin', 'manager', 'staff', 'cashier', 'waiter', 'customer', 'guest'],
  'order:update': ['admin', 'manager', 'staff', 'cashier', 'chef', 'waiter'],
  // Per-transition perms — role-gated state machine.
  'order:confirm':  ['admin', 'manager', 'cashier'],
  'order:prepare':  ['admin', 'manager', 'chef'],
  'order:ready':    ['admin', 'manager', 'chef'],
  'order:serve':    ['admin', 'manager', 'waiter', 'cashier'],
  // floor:view gates the admin-panel Floor tab specifically. Kept separate
  // from 'order:serve' so cashiers can still serve orders without seeing
  // the Floor tab in their side nav.
  'floor:view':     ['admin', 'manager', 'waiter'],
  'order:complete': ['admin', 'manager', 'cashier'],
  'order:addItems': ['admin', 'manager', 'cashier', 'waiter'],
  'order:cancel':   ['admin', 'manager', 'staff', 'cashier', 'customer'],
  'order:delete': ['admin', 'manager', 'staff'],

  'reservation:read':   ['admin', 'manager', 'staff', 'cashier', 'waiter', 'customer'],
  'reservation:create': ['admin', 'manager', 'staff', 'cashier', 'waiter', 'customer', 'guest'],
  'reservation:update': ['admin', 'manager', 'staff', 'cashier'],
  'reservation:cancel': ['admin', 'manager', 'staff', 'cashier', 'customer'],
  'reservation:delete': ['admin', 'manager', 'staff'],

  'table:read':   ['admin', 'manager', 'staff', 'cashier', 'waiter', 'customer'],
  'table:create': ['admin', 'manager'],
  'table:update': ['admin', 'manager'],
  'table:delete': ['admin', 'manager'],
  'table:status': ['admin', 'manager', 'staff', 'cashier', 'waiter'],

  'stock:read':     ['admin', 'manager'],
  'stock:create':   ['admin', 'manager'],
  'stock:update':   ['admin', 'manager'],
  'stock:delete':   ['admin', 'manager'],
  'stock:purchase': ['admin', 'manager'],
  'stock:adjust':   ['admin', 'manager'],

  'invoice:read':   ['admin', 'manager', 'staff', 'cashier'],
  'invoice:create': ['admin', 'manager', 'staff', 'cashier'],
  'invoice:update': ['admin', 'manager', 'staff'],
  'invoice:void':   ['admin', 'manager', 'staff'],
  'invoice:delete': ['admin', 'manager', 'staff'],

  'expense:read':   ['admin', 'manager', 'staff'],
  'expense:create': ['admin', 'manager', 'staff'],
  'expense:update': ['admin', 'manager', 'staff'],
  'expense:delete': ['admin', 'manager', 'staff'],

  'analytics:read':   ['admin', 'manager'],
  'analytics:export': ['admin', 'manager'],

  'message:read':   ['admin', 'manager', 'staff', 'cashier'],
  'message:update': ['admin', 'manager', 'staff'],
  'message:delete': ['admin', 'manager', 'staff'],

  'staff:read':       ['admin', 'manager'],
  'staff:create':     ['admin', 'manager'],
  'staff:update':     ['admin', 'manager'],
  'staff:changeRole': ['admin', 'manager'],
  'staff:delete':     ['admin'],

  'settings:read':   ['admin', 'manager'],
  'settings:update': ['admin', 'manager'],

  'notification:receive': ['admin', 'manager', 'cashier'],

  // Audit log — read-only by design. No write/delete permissions exist.
  'audit:read': ['admin', 'manager'],
};

/**
 * Check whether the given role has the given permission.
 * Pass `null` / `undefined` and we treat the user as 'guest'.
 */
export function roleHasPermission(role, permission) {
  const r = role || 'guest';
  const allowed = PERMISSIONS[permission];
  if (!allowed) return false;
  return allowed.includes(r);
}

/** Convenience for "is this user an admin-side worker?" */
export function isAdminPanelRole(role) {
  return ADMIN_PANEL_ROLES.includes(role);
}

/**
 * Pre-computed permission set for a role. Useful for showing the user a
 * "what you can do" summary, or to power the AccessGate component cheaply.
 */
export function permissionsForRole(role) {
  const r = role || 'guest';
  const out = [];
  for (const [perm, roles] of Object.entries(PERMISSIONS)) {
    if (roles.includes(r)) out.push(perm);
  }
  return out;
}

/** Human-friendly role labels for UI display. */
export const ROLE_LABELS = {
  admin: 'Administrator',
  manager: 'Manager',
  staff: 'Staff',
  cashier: 'Cashier',
  chef: 'Kitchen / Chef',
  waiter: 'Waiter',
  customer: 'Customer',
  guest: 'Guest',
};
